import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { seoHead } from "@/lib/seoHead";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/marketing/selo")({
  head: () => seoHead({ title: "Selo Marca do Bem | Admin Shopping do Bem", description: "Código HTML do selo para sites parceiros.", noindex: true }),
  component: SeloPage,
});

const SELO_HTML = `<!-- Início Selo Marca do Bem -->
<a
  href="https://shoppingdobem.com.br/seja-uma-marca-do-bem"
  target="_blank"
  rel="noopener noreferrer"
  aria-label="Conheça o programa Marca do Bem"
  style="display: inline-flex; align-items: center; justify-content: center; text-decoration: none;"
>
  <img
    src="https://shoppingdobem.com.br/selo-marca-do-bem.png"
    alt="Marca do Bem"
    width="100"
    height="100"
    loading="lazy"
    style="width: 96px; max-width: 100px; max-height: 100px; object-fit: contain; display: block;"
  />
</a>
<!-- Fim Selo Marca do Bem -->`;

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

function SeloPage() {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyText(SELO_HTML);
    if (ok) {
      setCopied(true);
      toast.success("Código HTML copiado!");
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast.error("Não foi possível copiar. Selecione o código manualmente.");
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Prévia do selo</CardTitle>
          <CardDescription>Exatamente como aparece no rodapé do Shopping do Bem.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center rounded-lg border bg-muted/30 p-10">
            <img
              src="/selo-marca-do-bem.png"
              alt="Marca do Bem"
              width={100}
              height={100}
              className="w-24 max-w-[100px] max-h-[100px] object-contain"
            />
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Cole o código ao lado no rodapé do site da marca parceira. O selo aparece automaticamente, sem precisar de CSS ou configuração extra.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Código HTML</CardTitle>
          <CardDescription>Pronto para colar no rodapé de qualquer site.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <pre className="max-h-96 overflow-auto rounded-lg border bg-muted p-4 text-xs leading-relaxed whitespace-pre-wrap break-all">
            <code>{SELO_HTML}</code>
          </pre>
          <Button onClick={handleCopy} className="w-full sm:w-auto">
            {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
            {copied ? "Copiado!" : "Copiar código HTML"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
